import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { getUsers, getPendingPayments, getPVPendingCounts, getNotifications, markNotificationRead, markAllNotificationsRead } from '../services/api';
import {
  FiGrid, FiUsers, FiCheckSquare, FiHeart, FiDollarSign, FiPackage,
  FiBarChart2, FiSettings, FiChevronDown, FiLogOut, FiSearch, FiMenu,
  FiUserPlus, FiList, FiBook, FiDownload, FiUpload, FiFileText,
  FiCreditCard, FiClipboard, FiUser, FiBell, FiInfo, FiAlertCircle, FiShield, FiRefreshCw,
  FiArrowLeft
} from 'react-icons/fi';
import LanguageSwitcher from './LanguageSwitcher';
import FontSizeSwitcher from './FontSizeSwitcher';
import ChangePasswordModal from './ChangePasswordModal';
import Toast from './Toast';

const PV_REMINDER_INTERVAL_MS = 10 * 60 * 1000; // toast reminder every 10 minutes while PVs are pending

const Layout = () => {
  const { t } = useTranslation();
  const { user, logout, canAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  // v1.9.69 — Auto-close the mobile drawer on any route change so tapping
  // a nav item doesn't leave the overlay covering the new page.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // Also re-sync when the viewport crosses the 768px breakpoint (zoom,
  // rotate, window resize). Keeps mobileOpen=false at desktop widths so
  // stale mobile-drawer state doesn't leak into desktop layout.
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 768) setMobileOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [pendingUsers, setPendingUsers] = useState(0);
  const [pendingPayments, setPendingPayments] = useState(0);
  const [pendingPVs, setPendingPVs] = useState(0);
  const [pvToast, setPvToast] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const lastPvToastAt = useRef(0);
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // v1.9.30 — top-bar refresh: fires 'app-refresh' event that pages opt into
  // to re-run their own load(). Also refreshes the header's own notifications
  // + PV counts. Spinner runs for 800 ms so short refreshes still feel tactile.
  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    try { window.dispatchEvent(new Event('app-refresh')); } catch (_) {}
    loadNotifications();
    setTimeout(() => setRefreshing(false), 800);
  };
  const notifUnread = notifs.filter(n => !n.read_at).length;

  const loadNotifications = () => {
    getNotifications().then(r => setNotifs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    getUsers().then(r => {
      const pending = (r.data || []).filter(u => !u.active && u.self_registered && !u.member_id).length;
      setPendingUsers(pending);
    }).catch(() => {});
    getPendingPayments().then(r => setPendingPayments(r.data?.length || 0)).catch(() => {});
    // PV pending counts — only Finance and Admin roles get a non-zero count from the backend.
    getPVPendingCounts().then(r => {
      const n = r.data?.total || 0;
      setPendingPVs(n);
      if (n > 0 && Date.now() - lastPvToastAt.current > PV_REMINDER_INTERVAL_MS) {
        lastPvToastAt.current = Date.now();
        const label = r.data.role === 'finance'
          ? `${n} payment voucher${n !== 1 ? 's' : ''} awaiting your Finance approval`
          : `${n} payment voucher${n !== 1 ? 's' : ''} awaiting your Admin approval`;
        setPvToast({ msg: label, type: 'warning' });
        setTimeout(() => setPvToast(null), 5000);
      }
    }).catch(() => {});
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const openNotif = (n) => {
    if (!n.read_at) {
      markNotificationRead(n.id).catch(() => {});
      setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
    }
    setNotifOpen(false);
    if (n.link) navigate(n.link);
  };
  const markAllRead = () => {
    markAllNotificationsRead().catch(() => {});
    setNotifs(prev => prev.map(x => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
  };
  const timeAgo = (ts) => {
    if (!ts) return '';
    const d = new Date(ts.replace(' ', 'T') + 'Z');
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  const getActiveGroup = (path) => {
    if (path.startsWith('/members')) return 'members';
    if (path.startsWith('/attendance')) return 'attendance';
    if (path.startsWith('/spiritual')) return 'spiritual';
    if (path.startsWith('/finance')) return 'finance';
    if (path.startsWith('/stock')) return 'stock';
    if (path.startsWith('/reports')) return 'reports';
    if (path.startsWith('/settings')) return 'settings';
    if (path.startsWith('/info')) return 'info';
    return null;
  };

  const [openGroups, setOpenGroups] = useState(() => {
    const active = getActiveGroup(window.location.pathname);
    return { members: active==='members', attendance: active==='attendance', spiritual: active==='spiritual', finance: active==='finance', stock: active==='stock', reports: active==='reports', settings: active==='settings', info: active==='info' };
  });

  const toggleGroup = (group) => {
    setOpenGroups(prev => {
      const allClosed = Object.keys(prev).reduce((a, k) => ({ ...a, [k]: false }), {});
      return { ...allClosed, [group]: !prev[group] };
    });
  };

  useEffect(() => {
    const active = getActiveGroup(location.pathname);
    if (active) setOpenGroups(prev => ({ ...Object.keys(prev).reduce((a, k) => ({ ...a, [k]: false }), {}), [active]: true }));
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (prefix) => location.pathname.startsWith(prefix);

  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

  const pageTitleKeys = {
    '/dashboard': 'nav.dashboard',
    '/members': 'nav.allMembers', '/members/add': 'nav.addMember',
    '/members/status': 'nav.membershipStatus',
    '/attendance/record': 'nav.recordAttendance', '/attendance/history': 'nav.attendanceHistory',
    '/attendance/absentees': 'nav.absenteeReport',
    '/spiritual/fathers': 'nav.spiritualFathers', '/spiritual/families': 'layout.allFamilies',
    '/spiritual/add-family': 'layout.addFamily', '/spiritual/followups': 'nav.followupRecords', '/spiritual/assignment': 'nav.assignMembers',
    '/finance/cr': 'nav.cr', '/finance/pv': 'nav.pv',
    '/finance/ap': 'nav.ap', '/finance/cashbook': 'nav.cashbook',
    '/stock/grn': 'nav.grn', '/stock/siv': 'nav.siv', '/stock/assets': 'nav.fixedAssets',
    '/reports/membership': 'layout.membershipReport',
    '/reports/spiritual-father': 'nav.spiritualFatherReport',
    '/reports/financial': 'nav.financialSummary',
    '/settings/profile': 'nav.churchProfile', '/settings/users': 'nav.usersRoles', '/settings/permissions': 'nav.permissions',
    '/settings/funds': 'nav.contributionTypes', '/settings/opening-balance': 'nav.openingBalance', '/settings/departments': 'nav.departments',
    '/settings/audit': 'Audit Log',
    '/info': 'nav.information',
  };
  const pageTitle = t(pageTitleKeys[location.pathname] || 'nav.dashboard');

  const initials = user?.name ? user.name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase() : 'AD';

  const NavItem = ({ path, icon, label }) => (
    <div className={`nav-item ${isActive(path) ? 'active' : ''}`} onClick={() => { navigate(path); if (window.innerWidth <= 768) setMobileOpen(false); }}>
      {icon}
      {!collapsed && <span>{label}</span>}
    </div>
  );

  const NavGroup = ({ group, icon, label, children }) => (
    <>
      <div className={`nav-group-header ${isGroupActive('/'+group) ? 'active' : ''}`} onClick={() => !collapsed && toggleGroup(group)}>
        <div>{icon}{!collapsed && <span style={{ marginLeft: 12 }}>{label}</span>}</div>
        {!collapsed && <FiChevronDown className={`chevron ${openGroups[group] ? 'open' : ''}`} />}
      </div>
      {!collapsed && openGroups[group] && <div className="nav-children">{children}</div>}
    </>
  );

  return (
    <div className="app-layout">
      {/* Sidebar */}
      {mobileOpen && <div onClick={() => setMobileOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 99, display: window.innerWidth <= 768 ? 'block' : 'none' }} />}
      <div className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src="/logo.png" alt="Logo" style={{ width: 36, height: 36, objectFit: 'contain' }} />
          </div>
          {!collapsed && (
            <div className="sidebar-brand">
              <h2>{t('layout.brand')}</h2>
              <p>{t('layout.brandTagline')}</p>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          <NavItem path="/dashboard" icon={<FiGrid className="nav-icon" />} label={t('nav.dashboard')} />

          {canAccess('members') && (
            <NavGroup group="members" icon={<FiUsers className="nav-icon" />} label={t('nav.members')}>
              <NavItem path="/members" icon={<FiList className="nav-icon" />} label={t('nav.allMembers')} />
              <NavItem path="/members/former" icon={<FiList className="nav-icon" />} label="Former Members" />
              <NavItem path="/members/add" icon={<FiUserPlus className="nav-icon" />} label={t('nav.addMember')} />
              <NavItem path="/members/status" icon={<FiBarChart2 className="nav-icon" />} label={t('nav.membershipStatus')} />
            </NavGroup>
          )}

          {canAccess('attendance') && (
            <NavGroup group="attendance" icon={<FiCheckSquare className="nav-icon" />} label={t('nav.attendance')}>
              <NavItem path="/attendance/record" icon={<FiCheckSquare className="nav-icon" />} label={t('nav.recordAttendance')} />
              <NavItem path="/attendance/history" icon={<FiList className="nav-icon" />} label={t('nav.attendanceHistory')} />
              <NavItem path="/attendance/absentees" icon={<FiFileText className="nav-icon" />} label={t('nav.absenteeReport')} />
            </NavGroup>
          )}

          {canAccess('spiritual') && (
            <NavGroup group="spiritual" icon={<FiHeart className="nav-icon" />} label={t('nav.spiritualCare')}>
              <NavItem path="/spiritual/fathers" icon={<FiUser className="nav-icon" />} label={t('nav.spiritualFathers')} />
              <NavItem path="/spiritual/assignment" icon={<FiUsers className="nav-icon" />} label={t('nav.assignMembers')} />
              <NavItem path="/spiritual/followups" icon={<FiClipboard className="nav-icon" />} label={t('nav.followupRecords')} />
            </NavGroup>
          )}

          {canAccess('finance') && (
            <NavGroup group="finance" icon={<FiDollarSign className="nav-icon" />} label={t('nav.finance')}>
              <NavItem path="/finance/cr" icon={<FiBook className="nav-icon" />} label={t('nav.cr')} />
              <NavItem path="/finance/approvals" icon={<FiCheckSquare className="nav-icon" />} label={t('nav.pendingApprovals')} />
              <NavItem path="/finance/pv" icon={<FiCreditCard className="nav-icon" />} label={t('nav.pv')} />
              <NavItem path="/finance/ap" icon={<FiFileText className="nav-icon" />} label={t('nav.ap')} />
              <NavItem path="/finance/cashbook" icon={<FiClipboard className="nav-icon" />} label={t('nav.cashbook')} />
            </NavGroup>
          )}

          {canAccess('stock') && (
            <NavGroup group="stock" icon={<FiPackage className="nav-icon" />} label={t('nav.stock')}>
              <NavItem path="/stock/grn" icon={<FiDownload className="nav-icon" />} label={t('nav.grn')} />
              <NavItem path="/stock/siv" icon={<FiUpload className="nav-icon" />} label={t('nav.siv')} />
              <NavItem path="/stock/assets" icon={<FiPackage className="nav-icon" />} label="Fixed Assets" />
            </NavGroup>
          )}

          {canAccess('reports') && (
            <NavGroup group="reports" icon={<FiBarChart2 className="nav-icon" />} label={t('nav.reports')}>
              <NavItem path="/reports/spiritual-father" icon={<FiHeart className="nav-icon" />} label={t('nav.spiritualFatherReport')} />
              <NavItem path="/reports/financial" icon={<FiDollarSign className="nav-icon" />} label={t('nav.financialSummary')} />
              <NavItem path="/reports/subscription-status" icon={<FiBell className="nav-icon" />} label="Subscription Status" />
            </NavGroup>
          )}

          {canAccess('settings') && (
            <NavGroup group="settings" icon={<FiSettings className="nav-icon" />} label={t('nav.settings')}>
              <NavItem path="/settings/profile" icon={<FiSettings className="nav-icon" />} label={t('nav.churchProfile')} />
              <NavItem path="/settings/users" icon={<FiUsers className="nav-icon" />} label={t('nav.usersRoles')} />
              <NavItem path="/settings/permissions" icon={<FiShield className="nav-icon" />} label="Permissions" />
              <NavItem path="/settings/funds" icon={<FiBell className="nav-icon" />} label={t('nav.contributionTypes')} />
              <NavItem path="/settings/opening-balance" icon={<FiBook className="nav-icon" />} label={t('nav.openingBalance', { defaultValue: 'Opening Balance' })} />
              <NavItem path="/settings/departments" icon={<FiList className="nav-icon" />} label={t('nav.departments')} />
              <NavItem path="/settings/audit" icon={<FiShield className="nav-icon" />} label="Audit Log" />
              <NavItem path="/settings/reset" icon={<FiSettings className="nav-icon" />} label="Reset Data" />
            </NavGroup>
          )}

          {canAccess('info') && (
            <NavItem path="/info" icon={<FiInfo className="nav-icon" />} label={t('nav.information')} />
          )}

          {/* Any staff user (Admin, Cashier, Finance, Secretary, Pastor,
              Cell Leader) who is also a linked church member gets a one-tap
              switch into their personal Member Portal. Same pattern that
              already worked for priest-admins — now extended to everyone. */}
          {user?.member_id && user?.role !== 'member' && (
            <NavItem path="/member/dashboard" icon={<FiHeart className="nav-icon" />} label="Switch to Member Portal" />
          )}
        </nav>

        {!collapsed && (
          <div className="sidebar-footer">
            <div className="user-info">
              <div className="user-avatar">{initials}</div>
              <div className="user-details">
                <h4>{user?.name}</h4>
                <p>{user?.role}</p>
              </div>
            </div>
            <button className="logout-btn" onClick={() => setShowPasswordModal(true)} style={{ marginBottom: 6 }}>
              🔒 {t('auth.changePassword')}
            </button>
            <button className="logout-btn" onClick={() => { logout(); navigate('/login'); }}>
              <FiLogOut /> {t('auth.logout')}
            </button>
          </div>
        )}
      </div>
      <ChangePasswordModal open={showPasswordModal} onClose={() => setShowPasswordModal(false)} />

      {/* Main */}
      <div className="main-content">
        <div className="top-header">
          <div className="top-header-left">
            <button className="sidebar-toggle" onClick={() => {
              if (window.innerWidth <= 768) setMobileOpen(!mobileOpen);
              else setCollapsed(!collapsed);
            }}><FiMenu /></button>
            {/* v1.9.63 — Back button. Hidden on the two root pages ('/' and
                '/dashboard'); everywhere else it walks the browser history
                back one step. */}
            {location.pathname !== '/' && location.pathname !== '/dashboard' && (
              <button
                className="sidebar-toggle"
                onClick={() => { window.history.length > 1 ? navigate(-1) : navigate('/dashboard'); }}
                title="Back"
                aria-label="Back"
              ><FiArrowLeft /></button>
            )}
            <h2>{pageTitle}</h2>
          </div>
          <div className="top-header-right">
            {/* Notification badges */}
            {pendingUsers > 0 && (
              <div
                onClick={() => navigate('/settings/users')}
                title={t('layout.pendingUsersTitle', { count: pendingUsers })}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#92400e', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                onMouseLeave={e => e.currentTarget.style.background = '#fffbeb'}
              >
                <FiUsers style={{ fontSize: 13 }} />
                <span>{t('layout.pendingUsersBadge', { count: pendingUsers })}</span>
                <span style={{ background: '#f59e0b', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>{pendingUsers}</span>
              </div>
            )}
            {pendingPayments > 0 && (
              <div
                onClick={() => navigate('/finance/approvals')}
                title={t('layout.pendingPaymentsTitle', { count: pendingPayments })}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#eef2ff', border: '1px solid #a5b4fc', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#3730a3', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#e0e7ff'}
                onMouseLeave={e => e.currentTarget.style.background = '#eef2ff'}
              >
                <FiDollarSign style={{ fontSize: 13 }} />
                <span>{t('layout.pendingPaymentsBadge', { count: pendingPayments })}</span>
                <span style={{ background: '#4f46e5', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>{pendingPayments}</span>
              </div>
            )}
            {pendingPVs > 0 && (
              <div
                onClick={() => navigate('/finance/pv?tab=pending')}
                title={`${pendingPVs} payment voucher${pendingPVs !== 1 ? 's' : ''} awaiting your approval`}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#991b1b', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fee2e2'}
                onMouseLeave={e => e.currentTarget.style.background = '#fef2f2'}
              >
                <FiCreditCard style={{ fontSize: 13 }} />
                <span>PV Approval</span>
                <span style={{ background: '#dc2626', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>{pendingPVs}</span>
              </div>
            )}
            {/* Notification bell */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setNotifOpen(o => !o)}
                title="Notifications"
                style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center', padding: 6 }}
              >
                <FiBell style={{ fontSize: 20 }} />
                {notifUnread > 0 && (
                  <span style={{ position: 'absolute', top: -2, right: -2, background: '#dc2626', color: '#fff', borderRadius: '50%', minWidth: 16, height: 16, fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
                    {notifUnread > 9 ? '9+' : notifUnread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <>
                  <div onClick={() => setNotifOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 998 }} />
                  <div style={isMobile
                    ? { position: 'fixed', top: 60, left: 10, right: 10, width: 'auto', maxHeight: '70vh', overflowY: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.18)', zIndex: 999 }
                    : { position: 'absolute', top: 36, right: 0, width: 340, maxHeight: 440, overflowY: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.18)', zIndex: 999 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid #f3f4f6', position: 'sticky', top: 0, background: '#fff' }}>
                      <strong style={{ fontSize: 14 }}>Notifications</strong>
                      {notifUnread > 0 && <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: '#4f46e5', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Mark all read</button>}
                    </div>
                    {notifs.length === 0 ? (
                      <div style={{ padding: '24px 14px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No notifications</div>
                    ) : notifs.map(n => (
                      <div
                        key={n.id}
                        onClick={() => openNotif(n)}
                        style={{ padding: '11px 14px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: n.read_at ? '#fff' : '#eff6ff', display: 'flex', gap: 10 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = n.read_at ? '#fff' : '#eff6ff'}
                      >
                        {!n.read_at && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#3b82f6', marginTop: 5, flexShrink: 0 }} />}
                        <div style={{ flex: 1, marginLeft: n.read_at ? 18 : 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{n.title}</div>
                          {n.body && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{n.body}</div>}
                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>{timeAgo(n.created_at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh this page"
              style={{ background: refreshing ? '#eef2ff' : 'none', border: 'none', cursor: refreshing ? 'wait' : 'pointer', color: refreshing ? '#4f46e5' : '#374151', display: 'flex', alignItems: 'center', padding: 8, borderRadius: 8 }}
            >
              <FiRefreshCw style={{ fontSize: 18, animation: refreshing ? 'app-refresh-spin 0.8s linear infinite' : 'none' }} />
            </button>
            <FontSizeSwitcher compact />
            <LanguageSwitcher compact />
            <div className="date-display">
              <div>{dateStr}</div>
              <div>{timeStr}</div>
            </div>
          </div>
        </div>
        <Outlet />
      </div>
      <Toast message={pvToast?.msg} type={pvToast?.type} onClose={() => setPvToast(null)} />
    </div>
  );
};

export default Layout;
